import React, { useState } from "react";
import { price, number } from "./model";
export default function BondCurve({
  sold,
  amount,
  tier,
  total = 200000000,
  available = 0,
  onAllocationChange,
  disabled = false,
}) {
  const [zoom, setZoom] = useState(false);
  const min = zoom ? Math.max(0, sold - Math.max(amount, 10000) * 0.2) : 0;
  const max = zoom
      ? sold + Math.max(amount, 10000) * 1.25
      : Math.max(total, sold + amount, sold, 10000),
    w = 720,
    h = 300,
    L = 64,
    R = 22,
    T = 30,
    B = 46;
  const x = (v) => L + ((w - L - R) * (v - min)) / (max - min),
    ceiling = price(max) * 1.08,
    y = (v) => h - B - ((h - T - B) * v) / ceiling;
  const points = Array.from(
    { length: 161 },
    (_, i) => min + ((max - min) * i) / 160,
  );
  const line = (t) =>
    points.map((v, i) => `${i ? "L" : "M"}${x(v)},${y(price(v, t))}`).join(" ");
  const end = Math.min(sold + amount, max);
  const selected = Array.from(
    { length: 60 },
    (_, i) => sold + ((end - sold) * i) / 59,
  );
  const fill =
    `M${x(sold)},${h - B} ` +
    selected.map((v) => `L${x(v)},${y(price(v, tier))}`).join(" ") +
    ` L${x(end)},${h - B} Z`;
  return (
    <figure className="bond-curve">
      <div className="bond-chart-controls">
        <button
          type="button"
          aria-pressed={zoom}
          onClick={() => setZoom(!zoom)}
        >
          {zoom ? "Show full curve" : "Zoom to my allocation"}
        </button>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={`Price curve. ${number(sold)} tokens sold. Selected allocation from ${number(sold)} to ${number(end)} tokens.`}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line
              x1={L}
              x2={w - R}
              y1={y(ceiling * t)}
              y2={y(ceiling * t)}
              stroke="#e2e8f0"
            />
            <text x={L - 10} y={y(ceiling * t) + 4} textAnchor="end">
              ${(ceiling * t).toFixed(zoom ? 6 : 4)}
            </text>
            <text x={x(min + (max - min) * t)} y={h - 19} textAnchor="middle">
              {new Intl.NumberFormat("en-US", {
                notation: "compact",
                maximumFractionDigits: 1,
              }).format(min + (max - min) * t)}
            </text>
          </g>
        ))}
        <path d={fill} fill="#cbd5ff" />
        <path
          d={line(0)}
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2"
          strokeDasharray="5 5"
        />
        <path d={line(tier)} fill="none" stroke="#4f46e5" strokeWidth="3" />
        <line
          x1={x(sold)}
          x2={x(sold)}
          y1={T}
          y2={h - B}
          stroke="#087f8c"
          strokeWidth="2"
        />
        <circle
          cx={x(sold)}
          cy={y(price(sold, tier))}
          r="5"
          fill="#087f8c"
          stroke="white"
          strokeWidth="2"
        />
        <text
          x={Math.max(L + 8, Math.min(w - 120, x(sold) + 8))}
          y={T - 10}
          className="bond-chart-now"
        >
          Current position
        </text>
        {amount > 0 && (
          <line
            x1={x(end)}
            x2={x(end)}
            y1={y(price(end, tier))}
            y2={h - B}
            stroke="#4f46e5"
            strokeDasharray="4 3"
          />
        )}
      </svg>
      {onAllocationChange && (
        <div className="bond-chart-slider">
          <label htmlFor="bond-allocation-slider">
            <span>Slide to choose your allocation</span>
            <strong>{number(amount)} ADBYTES</strong>
          </label>
          <input
            id="bond-allocation-slider"
            type="range"
            min="0"
            max="10000"
            step="1"
            value={
              available > 0
                ? Math.min(10000, Math.max(0, (amount / available) * 10000))
                : 0
            }
            disabled={disabled || available <= 0}
            aria-valuetext={`${number(amount)} ADBYTES`}
            onChange={(event) => onAllocationChange(Number(event.target.value))}
          />
          <div className="bond-slider-limits">
            <span>0</span>
            <span>{number(available)} available</span>
          </div>
        </div>
      )}
      <figcaption>
        <span>
          <i className="legend-current" />
          Current position
        </span>
        <span>
          <i className="legend-selected" />
          Your allocation
        </span>
        <span>
          <i className="legend-line" />
          Selected schedule
        </span>
        <span>Dashed: immediate price · USDC</span>
      </figcaption>
      <p className="bond-caption">
        Shading shows your allocation. Your price covers the whole segment.
      </p>
    </figure>
  );
}
